import React from 'react'

/*
 * CareFlow — a 4-step process strip used on condition pages (and on
 * the marketing home page) to reduce "what happens next?" drop-off.
 *
 * The visual is deliberately calm: small numbered tokens, a faint
 * connecting track, and concise one-liners per step. It reads left-
 * to-right on desktop and stacks vertically on mobile, where the
 * connecting track becomes a left-aligned rail instead.
 *
 * Variants:
 *   - "default" (light surface, violet accents) for condition pages
 *     and section placements on light backgrounds.
 *   - "dark" inverts the palette for embedding inside dark CTAs.
 */

const STEPS = [
  {
    id: 'consultation',
    title: 'Consultation',
    body:
      'A care coordinator calls (or WhatsApps, your choice). You tell us what\u2019s going on — no forms, no pressure.',
    Icon: (p) => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" {...p}>
        <path d="M3 5a2 2 0 0 1 2-2h2.28a1 1 0 0 1 .95.68l1 2.93a1 1 0 0 1-.25 1L7.91 9.09a11 11 0 0 0 6 6l1.48-1.07a1 1 0 0 1 1-.25l2.93 1a1 1 0 0 1 .68.95V18a2 2 0 0 1-2 2A17 17 0 0 1 3 5z" />
      </svg>
    ),
  },
  {
    id: 'expert-consultation',
    title: 'Expert Psychiatrist',
    body:
      'We book you with a psychiatrist who is a leading authority in this condition — not a generalist.',
    Icon: (p) => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" {...p}>
        <path d="M12 2l2.39 4.84L20 8l-4 3.9L17.18 18 12 15.27 6.82 18 8 11.9 4 8l5.61-1.16z" />
      </svg>
    ),
  },
  {
    id: 'assessment',
    title: 'Assessment',
    body:
      'If the psychiatrist recommends it, a senior clinical psychologist runs in-depth assessments before we shape the plan.',
    Icon: (p) => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" {...p}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="13" y2="17" />
      </svg>
    ),
  },
  {
    id: 'therapy',
    title: 'Therapy + Medicine',
    body:
      'Structured therapy with a senior clinical psychologist when indicated, plus medication adherence support — both coordinated by the same team.',
    Icon: (p) => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" {...p}>
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    ),
  },
  {
    id: 'ongoing',
    title: 'Ongoing care',
    body:
      'Medication reviews, therapy adjustments, and continuity of care — the same team stays with you as things evolve.',
    Icon: (p) => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" {...p}>
        <polyline points="17 1 21 5 17 9" />
        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
        <polyline points="7 23 3 19 7 15" />
        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
      </svg>
    ),
  },
]

export default function CareFlow({
  variant = 'default',
  title = 'What happens when you reach out',
  sub = 'No black box — here\u2019s exactly how care unfolds.',
  compact = false,
}) {
  return (
    <section
      className={`cf-strip${variant === 'dark' ? ' cf-strip--dark' : ''}${
        compact ? ' cf-strip--compact' : ''
      }`}
      aria-label="Care process"
    >
      {title || sub ? (
        <div className="cf-head">
          {title ? <h3 className="cf-title">{title}</h3> : null}
          {sub ? <p className="cf-sub">{sub}</p> : null}
        </div>
      ) : null}
      <ol className="cf-steps">
        {STEPS.map((step, i) => (
          <li key={step.id} className="cf-step">
            <div className="cf-step-node">
              <span className="cf-step-number" aria-hidden="true">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="cf-step-icon" aria-hidden="true">
                <step.Icon width={20} height={20} />
              </span>
            </div>
            <div className="cf-step-body">
              <div className="cf-step-title">{step.title}</div>
              <p className="cf-step-text">{step.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}
